/*
 * Euroville — visualizador de giro 360 (exterior)
 * Mesmo código usado no protótipo local e na seção do tema (assets/euro-giro-360.js).
 * Sem dependências. Um elemento <euro-giro-360> por página.
 *
 * Marcação esperada:
 *   <euro-giro-360 data-frames='["url-000", ... "url-350"]' data-frames-mobile='[...]' data-start="0">
 *     <img class="eg360__frame" ...>   (quadro inicial, já no HTML para LCP/SEO)
 *     ...controles...
 *   </euro-giro-360>
 *
 * Convenção dos quadros: 000 = frente, 090 = lateral com a frente à esquerda,
 * 180 = traseira, 270 = lateral com a frente à direita (README do projeto-360).
 */
(function () {
  if (customElements.get('euro-giro-360')) return;

  const VOLTAS_POR_LARGURA = 1.25;  // arrastar a largura inteira do palco gira 1¼ volta
  const ATRITO = 0.9;               // inércia ao soltar
  const MQ_MOBILE = '(max-width: 749px)';

  class EuroGiro360 extends HTMLElement {
    connectedCallback() {
      const mobile = window.matchMedia(MQ_MOBILE).matches;
      const raw = (mobile && this.dataset.framesMobile) || this.dataset.frames;
      this.frames = JSON.parse(raw || '[]');
      this.total = this.frames.length;
      if (this.total < 2) return;
      // Quadros 1920: o celular só os usa em tela cheia.
      this.framesHi = JSON.parse(this.dataset.frames || raw);
      this.amp = 1; this.tx = 0; this.ty = 0;  // zoom por pinça, só em tela cheia

      this.idx = parseInt(this.dataset.start || '0', 10) % this.total;
      this.img = this.querySelector('.eg360__frame');
      this.stage = this.querySelector('.eg360__stage');
      this.bar = this.querySelector('.eg360__progress-bar');
      this.hint = this.querySelector('.eg360__hint');
      this.cache = new Array(this.total);
      this.loaded = 0;
      this.ready = false;
      this.vel = 0;

      // Zoom por modelo (o configurador BMW deixa margem larga); vem do metaobjeto.
      this.zoomBase = Math.max(1, parseFloat(this.dataset.zoom || '1'));
      if (this.zoomBase > 1) this.style.setProperty('--eg360-zoom', String(this.zoomBase));
      // No celular o quadro inicial já sai do conjunto mobile.
      if (this.img && this.img.getAttribute('src') !== this.frames[this.idx]) {
        this.img.removeAttribute('srcset');
        this.img.src = this.frames[this.idx];
      }

      this.setAttribute('role', 'slider');
      this.setAttribute('tabindex', '0');
      this.setAttribute('aria-valuemin', '0');
      this.setAttribute('aria-valuemax', String(this.total - 1));
      this._aria();

      // Carrega só quando a seção chega perto da tela.
      const io = new IntersectionObserver((ents) => {
        if (ents.some((e) => e.isIntersecting)) { io.disconnect(); this._preload(); }
      }, { rootMargin: '300px' });
      io.observe(this);

      this._bind();
    }

    _preload() {
      this.classList.add('is-loading');
      // Ordem de carga: a partir do quadro inicial, alternando os dois lados,
      // para que o arrasto já funcione cedo perto da vista inicial.
      const ordem = [this.idx];
      for (let d = 1; ordem.length < this.total; d++) {
        ordem.push((this.idx + d) % this.total);
        if (ordem.length < this.total) ordem.push((this.idx - d + this.total) % this.total);
      }
      ordem.forEach((i) => {
        const im = new Image();
        im.decoding = 'async';
        im.onload = im.onerror = (ev) => {
          if (i === ordem[0] && ev.type === 'load') this._detectarFundo(im);
          this.loaded++;
          if (this.bar) this.bar.style.width = (100 * this.loaded / this.total) + '%';
          if (this.loaded === this.total) this._pronto();
        };
        im.src = this.frames[i];
        this.cache[i] = im;
      });
    }

    _pronto() {
      this.ready = true;
      this.classList.remove('is-loading');
      this.classList.add('is-ready');
      const reduz = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!reduz && this.dataset.intro !== 'false') this._intro();
    }

    // Meia volta de apresentação ao ficar pronto, para mostrar que o carro gira.
    _intro() {
      const passos = Math.round(this.total / 4);
      let n = 0;
      const tick = () => {
        if (this.interagiu || n >= passos * 2) return;
        this._ir(n < passos ? this.idx + 1 : this.idx - 1);
        n++;
        setTimeout(tick, 45);
      };
      setTimeout(tick, 400);
    }

    _ir(i) {
      this.idx = ((Math.round(i) % this.total) + this.total) % this.total;
      const c = this.cache[this.idx];
      const src = c && c.complete && c.naturalWidth ? c.src : this.frames[this.idx];
      if (this.img.src !== src) this.img.src = src;
      this._aria();
    }

    _aria() {
      const graus = Math.round(this.idx * 360 / this.total);
      this.setAttribute('aria-valuenow', String(this.idx));
      this.setAttribute('aria-valuetext', graus + ' graus');
    }

    _bind() {
      let x0 = 0, i0 = 0, ultimoX = 0, ultimoT = 0, ativo = false, px = 10, toque = null, moveu = false;
      let pinca = null;
      const alvo = this.stage || this;
      const dedos = new Map();
      const dois = () => [...dedos.values()];
      const dist = () => { const [a, b] = dois(); return Math.hypot(a.x - b.x, a.y - b.y); };
      const meio = () => { const [a, b] = dois(); return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }; };
      const emCheia = () => this.classList.contains('is-tela-cheia');

      alvo.addEventListener('pointerdown', (e) => {
        if (e.button !== undefined && e.button !== 0) return;
        dedos.set(e.pointerId, { x: e.clientX, y: e.clientY });
        alvo.setPointerCapture(e.pointerId);
        this.interagiu = true; this.vel = 0;
        if (this.hint) this.hint.classList.add('is-hidden');
        if (dedos.size === 2 && emCheia()) {  // pinça: para de girar e amplia
          ativo = false; this.classList.remove('is-dragging');
          pinca = { d0: dist(), amp0: this.amp, m0: meio(), tx0: this.tx, ty0: this.ty };
          return;
        }
        if (dedos.size > 1) return;
        ativo = true; moveu = false;
        px = Math.max(4, alvo.clientWidth / (this.total * VOLTAS_POR_LARGURA));
        x0 = ultimoX = e.clientX; i0 = this.idx; ultimoT = performance.now();
        this.classList.add('is-dragging');
      });
      alvo.addEventListener('pointermove', (e) => {
        if (!dedos.has(e.pointerId)) return;
        dedos.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pinca && dedos.size === 2) {
          const m = meio();
          this._ampliar(pinca.amp0 * dist() / pinca.d0, pinca.tx0 + m.x - pinca.m0.x, pinca.ty0 + m.y - pinca.m0.y);
          return;
        }
        if (!ativo) return;
        if (Math.abs(e.clientX - x0) > 8) moveu = true;
        const agora = performance.now();
        // Arrastar para a esquerda leva a frente para a esquerda (sentido crescente: 000 → 090).
        this._ir(i0 - (e.clientX - x0) / px);
        this.vel = -(e.clientX - ultimoX) / px / Math.max(1, agora - ultimoT) * 16;
        ultimoX = e.clientX; ultimoT = agora;
      });
      const fim = (e) => {
        dedos.delete(e.pointerId);
        if (pinca) {  // fim da pinça: só volta a girar com um toque novo
          if (dedos.size === 0) pinca = null;
          if (this.amp < 1.05) this._ampliar(1);
          return;
        }
        if (!ativo) return;
        ativo = false;
        this.classList.remove('is-dragging');
        // Toque duplo em tela cheia: amplia 2,5× no ponto tocado; outro toque duplo volta.
        if (e.type === 'pointerup' && !moveu && e.pointerType === 'touch' && emCheia()) {
          const agora = performance.now();
          if (toque && agora - toque.t < 350 && Math.hypot(e.clientX - toque.x, e.clientY - toque.y) < 30) {
            toque = null;
            if (this.amp > 1) this._ampliar(1);
            else {
              const r = alvo.getBoundingClientRect();
              this._ampliar(2.5, -(e.clientX - r.left - r.width / 2) * 1.5, -(e.clientY - r.top - r.height / 2) * 1.5);
            }
            return;
          }
          toque = { t: agora, x: e.clientX, y: e.clientY };
        }
        this._inercia();
      };
      alvo.addEventListener('pointerup', fim);
      alvo.addEventListener('pointercancel', fim);

      // Botão de tela cheia: o palco ocupa a tela inteira e o giro continua funcionando.
      const cheia = this.querySelector('[data-eg360-cheia]');
      if (cheia) {
        cheia.addEventListener('pointerdown', (e) => e.stopPropagation());
        cheia.addEventListener('click', () => { this.interagiu = true; this._telaCheia(!this.classList.contains('is-tela-cheia')); });
        document.addEventListener('fullscreenchange', () => {
          if (!document.fullscreenElement && this.classList.contains('is-tela-cheia')) this._telaCheia(false);
        });
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.classList.contains('is-tela-cheia')) this._telaCheia(false);
        });
      }

      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { this.interagiu = true; this._ir(this.idx + 1); e.preventDefault(); }
        if (e.key === 'ArrowLeft')  { this.interagiu = true; this._ir(this.idx - 1); e.preventDefault(); }
        if (e.key === 'Home')       { this.interagiu = true; this._ir(0); e.preventDefault(); }
      });

      this.querySelectorAll('[data-eg360-step]').forEach((b) => {
        b.addEventListener('click', () => {
          this.interagiu = true;
          this._ir(this.idx + parseInt(b.dataset.eg360Step, 10));
        });
      });
      // Botão de fundo claro/escuro (como no site da GWM). A escolha fica lembrada no navegador.
      const tema = this.querySelector('[data-eg360-tema]');
      if (tema) {
        tema.addEventListener('pointerdown', (e) => e.stopPropagation());
        tema.addEventListener('click', () => this._escuro(!this.classList.contains('is-escuro'), true));
        let salvo = null;
        try { salvo = localStorage.getItem('eg360-fundo'); } catch (e) {}
        if (salvo === 'escuro') this._escuro(true, false);
      }
      this.querySelectorAll('[data-eg360-goto]').forEach((b) => {
        b.addEventListener('click', () => {
          this.interagiu = true;
          const graus = parseInt(b.dataset.eg360Goto, 10);
          this._animarAte(Math.round(graus / 360 * this.total));
        });
      });
    }

    // Tela cheia. Usa a API nativa quando existe (Android, computador) e, no iPhone, que não a tem
    // para elementos, fixa o palco sobre a página. No celular troca para os quadros 1920.
    _telaCheia(ligar) {
      this.classList.toggle('is-tela-cheia', ligar);
      document.documentElement.classList.toggle('eg360-trava-rolagem', ligar);
      const b = this.querySelector('[data-eg360-cheia]');
      if (b) b.setAttribute('aria-label', ligar ? 'Sair da tela cheia' : 'Ver em tela cheia');
      const st = this.stage;
      if (ligar) {
        if (st.requestFullscreen && !document.fullscreenElement) {
          st.requestFullscreen({ navigationUI: 'hide' }).then(() => {
            try { screen.orientation.lock('landscape').catch(() => {}); } catch (e) {}
          }).catch(() => {});
        }
        if (this.frames !== this.framesHi) {  // celular: quadros em alta para a tela inteira
          this.frames = this.framesHi;
          this.cache = this.frames.map((u) => { const im = new Image(); im.decoding = 'async'; im.src = u; return im; });
          this._ir(this.idx);
        }
      } else {
        this._ampliar(1);
        if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
        try { screen.orientation.unlock(); } catch (e) {}
      }
    }

    // Zoom de 1× a 3× sobre o zoom-base do modelo; tx/ty em px, limitados para não sobrar borda vazia.
    _ampliar(amp, tx = 0, ty = 0) {
      this.amp = Math.min(3, Math.max(1, amp));
      if (this.amp < 1.02) { this.amp = 1; tx = ty = 0; }
      const w = this.stage.clientWidth, h = this.stage.clientHeight, s = this.zoomBase * this.amp;
      const mx = Math.max(0, w * (s - 1) / 2), my = Math.max(0, h * (s - 1) / 2);
      this.tx = Math.min(mx, Math.max(-mx, tx)); this.ty = Math.min(my, Math.max(-my, ty));
      this.img.style.transform = this.amp === 1 ? '' : `translate(${this.tx}px, ${this.ty}px) scale(${s})`;
      this.classList.toggle('is-ampliado', this.amp > 1);
    }

    _escuro(ligar, salvar) {
      this.classList.toggle('is-escuro', ligar);
      const b = this.querySelector('[data-eg360-tema]');
      if (b) {
        b.setAttribute('aria-pressed', String(ligar));
        b.setAttribute('aria-label', ligar ? 'Clarear o fundo' : 'Escurecer o fundo');
      }
      if (salvar) { try { localStorage.setItem('eg360-fundo', ligar ? 'escuro' : 'claro'); } catch (e) {} }
    }

    // O botão de fundo só aparece com quadros de fundo transparente (escurece só o fundo, nunca o carro).
    // Lê um pixel do canto do quadro inicial.
    _detectarFundo(im) {
      try {
        const c = document.createElement('canvas');
        c.width = c.height = 1;
        const ctx = c.getContext('2d');
        ctx.drawImage(im, 0, 0, 1, 1, 0, 0, 1, 1);
        if (ctx.getImageData(0, 0, 1, 1).data[3] < 250) this.classList.add('is-transparente');
      } catch (e) { /* origem diferente: botão fica oculto */ }
    }

    _inercia() {
      let pos = this.idx;
      const passo = () => {
        if (Math.abs(this.vel) < 0.05) return;
        pos += this.vel; this.vel *= ATRITO;
        this._ir(pos);
        requestAnimationFrame(passo);
      };
      requestAnimationFrame(passo);
    }

    // Caminho mais curto até a vista pedida (frente, lateral, traseira).
    _animarAte(destino) {
      let d = ((destino - this.idx) % this.total + this.total) % this.total;
      if (d > this.total / 2) d -= this.total;
      const sinal = Math.sign(d);
      let falta = Math.abs(d);
      const tick = () => {
        if (falta-- <= 0) return;
        this._ir(this.idx + sinal);
        setTimeout(tick, 35);
      };
      tick();
    }
  }

  customElements.define('euro-giro-360', EuroGiro360);
})();
